/**
 * First-class multi-format layout engine UI for REEL&SHORTS.
 * Format switcher + locked Screen Composition preview + Replan + Generate all formats.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Ratio, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import {
  REELS_FORMAT_CANVASES,
  activeComposition,
  formatMeta,
  layoutColumnsLabel,
  previewAspectStyle,
  sanitizeFormatsBlock,
} from '@/lib/reelsShortsLayoutEngine';
import { Button } from '@/components/ui/button';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function LockedCompositionPreview({ plan, active }) {
  const canvas = plan?.canvas || {};
  const elements = plan?.elements || {};
  const video = elements.video || {};
  const broll = elements.broll;
  const sub = elements.subtitle || {};
  const style = previewAspectStyle(canvas.aspect_ratio || '9:16', 112);
  const layout = String(video.layout || 'fullscreen');
  const isSplit = layout === 'split';
  const isHSplit = layout === 'split_horizontal';
  const isTriple = layout === 'triple';

  return (
    <div
      className={`relative overflow-hidden rounded-lg border ${
        active ? 'border-rose-400/60 bg-rose-500/10' : 'border-white/10 bg-black/40'
      }`}
      style={style}
      data-testid="reels-layout-composition-preview"
    >
      {isTriple ? (
        <div className="absolute inset-0 flex flex-col">
          <div className="flex-[0.22] bg-amber-500/20" />
          <div className="flex-[0.56] bg-cyan-500/30 flex items-center justify-center text-[8px] uppercase text-cyan-100">
            speaker
          </div>
          <div className="flex-[0.22] bg-amber-500/20" />
        </div>
      ) : (
        <div className={`absolute inset-0 flex ${isHSplit ? 'flex-col' : 'flex-row'}`}>
          <div
            className={`flex items-center justify-center text-[8px] uppercase tracking-wide bg-cyan-500/25 text-cyan-100 ${
              isSplit || isHSplit ? (isHSplit ? 'h-1/2' : 'w-1/2') : 'w-full h-full'
            }`}
          >
            speaker
          </div>
          {broll ? (
            <div
              className={`flex items-center justify-center text-[8px] uppercase tracking-wide bg-amber-500/20 text-amber-100 ${
                isHSplit ? 'h-1/2' : 'w-1/2'
              }`}
            >
              broll
            </div>
          ) : null}
        </div>
      )}
      {sub?.placement ? (
        <div
          className={`absolute left-[5%] right-[5%] h-[10%] rounded-sm bg-white/25 ${
            sub.placement === 'top'
              ? 'top-[8%]'
              : sub.placement === 'center'
                ? 'top-[45%]'
                : 'bottom-[8%]'
          }`}
        />
      ) : null}
      {plan?.status === 'locked' ? (
        <span className="absolute top-0.5 right-0.5 rounded bg-emerald-500/80 px-1 text-[7px] font-semibold text-black">
          locked
        </span>
      ) : null}
    </div>
  );
}

function CompositionPreview({ composition, lockedPlan, active }) {
  if (lockedPlan) {
    return <LockedCompositionPreview plan={lockedPlan} active={active} />;
  }
  const canvas = composition?.canvas || {};
  const layout = composition?.layout || {};
  const columns = layout.columns || [{ width: 1, content: 'speaker' }];
  const style = previewAspectStyle(canvas.aspect_ratio || '9:16', 112);
  const isSplit = String(layout.type || '') === 'split';
  const isHSplit = String(layout.type || '') === 'split_horizontal';

  return (
    <div
      className={`relative overflow-hidden rounded-lg border ${
        active ? 'border-rose-400/60 bg-rose-500/10' : 'border-white/10 bg-black/40'
      }`}
      style={style}
      data-testid="reels-layout-composition-preview"
    >
      <div className={`absolute inset-0 flex ${isHSplit ? 'flex-col' : 'flex-row'}`}>
        {columns.map((col, idx) => {
          const frac = Math.max(0.08, Number(col.width) || 1 / columns.length);
          return (
            <div
              key={`${col.content}-${idx}`}
              className={`flex items-center justify-center text-[8px] uppercase tracking-wide ${
                col.content === 'speaker'
                  ? 'bg-cyan-500/25 text-cyan-100'
                  : col.content === 'broll'
                    ? 'bg-amber-500/20 text-amber-100'
                    : 'bg-white/10 text-slate-300'
              }`}
              style={
                isHSplit || isSplit || columns.length > 1
                  ? isHSplit
                    ? { height: `${frac * 100}%` }
                    : { width: `${frac * 100}%` }
                  : { width: '100%', height: '100%' }
              }
            >
              {col.content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ReelsShortsLayoutEngine({
  projectId,
  formats: formatsProp = null,
  screenCompositions = null,
  snippets = [],
  disabled = false,
  onUpdated,
  onGenerateAllFormats = null,
  generatingAll = false,
  compact = false,
}) {
  const [formats, setFormats] = useState(() => sanitizeFormatsBlock(formatsProp));
  const [saving, setSaving] = useState('');

  useEffect(() => {
    setFormats(sanitizeFormatsBlock(formatsProp));
  }, [formatsProp, projectId]);

  const active = formats.active || '9:16';
  const composition = useMemo(() => activeComposition(formats), [formats]);
  const exportTargets = formats.export_targets || [];
  const firstShotId = String(snippets?.[0]?.id || '').trim();

  const lockedByFormat = useMemo(() => {
    const byShot = screenCompositions?.by_shot || {};
    const out = {};
    REELS_FORMAT_CANVASES.forEach((row) => {
      const shotMap = firstShotId ? byShot[firstShotId] : null;
      out[row.id] = shotMap?.[row.id] || null;
    });
    return out;
  }, [screenCompositions, firstShotId]);

  const patch = async (body, busyKey, { optimisticFormats = null } = {}) => {
    if (!projectId) return null;
    if (optimisticFormats) {
      setFormats(sanitizeFormatsBlock(optimisticFormats));
    }
    setSaving(busyKey);
    try {
      const { data } = await api.patch(`/reels-shorts/projects/${projectId}/edit-plan`, body);
      const nextFormats = sanitizeFormatsBlock(data?.project?.edit_plan?.formats || data?.edit_plan?.formats);
      setFormats(nextFormats);
      onUpdated?.(data?.project || null, nextFormats);
      return data?.project || null;
    } catch (err) {
      if (optimisticFormats) {
        setFormats(sanitizeFormatsBlock(formatsProp));
      }
      toast.error(formatApiError(err, 'Could not update layout format'));
      return null;
    } finally {
      setSaving('');
    }
  };

  const selectFormat = async (id) => {
    if (disabled || id === active || saving === 'format') return;
    const next = await patch({ active_format: id }, 'format');
    if (next) toast.success(`Format · ${formatMeta(id).label}`);
  };

  const reframe = async () => {
    if (disabled || saving) return;
    const next = await patch({ reframe_format: active }, 'reframe');
    if (next) toast.success(`AI Reframe ready for ${formatMeta(active).shortLabel}`);
  };

  const replan = async () => {
    if (disabled || !firstShotId || saving) return;
    const next = await patch(
      {
        replan_screen_composition: { shot_id: firstShotId, format: active },
        plan_all_formats: !screenCompositions?.by_shot,
      },
      'replan',
    );
    if (next) toast.success(`Replanned ${formatMeta(active).shortLabel} composition`);
  };

  const planAll = async () => {
    if (disabled || saving) return;
    const next = await patch({ plan_all_formats: true }, 'plan-all');
    if (next) toast.success('Locked compositions for all export targets');
  };

  const toggleExport = async (id) => {
    if (saving === 'export') return;
    const set = new Set(exportTargets);
    if (set.has(id)) {
      if (set.size <= 1) {
        toast.message('Keep at least one export format selected');
        return;
      }
      set.delete(id);
    } else {
      set.add(id);
    }
    const nextTargets = [...set];
    const optimistic = { ...formats, export_targets: nextTargets };
    await patch({ export_targets: nextTargets }, 'export', { optimisticFormats: optimistic });
  };

  return (
    <div
      className={`rounded-xl border border-rose-400/20 bg-rose-500/[0.05] ${compact ? 'p-3 space-y-2' : 'p-4 space-y-3'}`}
      data-testid="reels-layout-engine"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-white flex items-center gap-1.5">
            <Ratio className="w-3.5 h-3.5 text-rose-300" />
            Layout engine
          </p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">
            Independent locked compositions per format — shared master content.
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled || Boolean(saving) || !firstShotId}
            onClick={() => void replan()}
            className="h-8 text-[11px]"
            data-testid="reels-layout-replan"
          >
            {saving === 'replan' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
            )}
            Replan
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={disabled || Boolean(saving)}
            onClick={() => void reframe()}
            className="h-8 text-[11px]"
            data-testid="reels-layout-ai-reframe"
          >
            {saving === 'reframe' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            ) : (
              <TwinneeLogo className="w-3.5 h-3.5 mr-1" />
            )}
            AI Reframe
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5" data-testid="reels-layout-format-switcher">
        {REELS_FORMAT_CANVASES.map((row) => {
          const selected = active === row.id;
          return (
            <button
              key={row.id}
              type="button"
              disabled={disabled || Boolean(saving)}
              onClick={() => void selectFormat(row.id)}
              className={`rounded-lg border px-1.5 py-2 text-center transition cursor-pointer disabled:opacity-50 ${
                selected
                  ? 'border-rose-400/50 bg-rose-500/15 text-white'
                  : 'border-white/10 bg-black/25 text-[#CBD5E1] hover:border-white/25'
              }`}
              data-testid={`reels-layout-format-${row.id.replace(':', '-')}`}
            >
              <div className="mx-auto mb-1.5 flex justify-center">
                <CompositionPreview
                  composition={formats.compositions?.[row.id]}
                  lockedPlan={lockedByFormat[row.id]}
                  active={selected}
                />
              </div>
              <p className="text-[10px] font-semibold leading-tight">{row.shortLabel}</p>
              <p className="text-[9px] text-[#6B7280]">{row.id}</p>
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 space-y-1">
        <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Active composition</p>
        <p className="text-[11px] text-[#E5E7EB]">
          {formatMeta(active).label} · {composition?.canvas?.width}×{composition?.canvas?.height}
          {' · '}
          {lockedByFormat[active]?.chosen
            || lockedByFormat[active]?.elements?.video?.layout
            || composition?.layout?.type
            || 'stack'}
          {lockedByFormat[active]?.status === 'locked' ? ' · locked' : ''}
        </p>
        <p className="text-[10px] text-[#9CA3AF] truncate">
          {lockedByFormat[active]
            ? `Subtitle ${lockedByFormat[active]?.elements?.subtitle?.placement || 'bottom'}${
                lockedByFormat[active]?.elements?.broll ? ' · B-roll pane' : ''
              }`
            : layoutColumnsLabel(composition?.layout)}
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">Generate all formats</p>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || Boolean(saving)}
            onClick={() => void planAll()}
            className="h-7 text-[10px] text-slate-300"
            data-testid="reels-layout-plan-all"
          >
            {saving === 'plan-all' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Lock plans
          </Button>
        </div>
        <p className="text-[10px] text-[#9CA3AF]">
          Encodes every checked ratio below. Different from &quot;Re-export active format&quot;
          (one canvas only) on Final reel.
        </p>
        <div className="flex flex-wrap gap-1.5" data-testid="reels-layout-export-targets">
          {REELS_FORMAT_CANVASES.map((row) => {
            const on = exportTargets.includes(row.id);
            return (
              <label
                key={row.id}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] cursor-pointer select-none ${
                  on
                    ? 'border-rose-400/40 bg-rose-500/10 text-rose-100'
                    : 'border-white/10 text-[#9CA3AF] hover:border-white/25'
                } ${saving === 'export' ? 'opacity-70' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={saving === 'export'}
                  onChange={(event) => {
                    event.stopPropagation();
                    void toggleExport(row.id);
                  }}
                  className="rounded border-white/20"
                  data-testid={`reels-layout-export-${row.id.replace(':', '-')}`}
                />
                {row.id} — {row.shortLabel}
              </label>
            );
          })}
        </div>
        {onGenerateAllFormats ? (
          <Button
            type="button"
            size="sm"
            disabled={disabled || generatingAll || exportTargets.length < 1}
            onClick={() => onGenerateAllFormats(exportTargets)}
            className="w-full h-9 mt-1 bg-rose-500 hover:bg-rose-400 text-black"
            data-testid="reels-layout-generate-all"
          >
            {generatingAll ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Download className="w-3.5 h-3.5 mr-1.5" />
            )}
            Generate all formats ({exportTargets.length})
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default ReelsShortsLayoutEngine;
